import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  Injector,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  booleanAttribute,
  inject,
} from '@angular/core';
import { NgClass } from '@angular/common';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import clsx from 'clsx';
import { IconCalendarComponent, IconInfoComponent } from '@pedix-workspace/angular-ui-icons';
import {
  WithDefaultValidatorProps,
  composeValidatorFn,
  hasChangedAnyDefaultValidatorProps,
} from '../utils/defaultValidator';
import { InputMaskModule, InputmaskOptions, createMask } from '@ngneat/input-mask';
import { format, parse, isValid, isBefore, isEqual, isAfter } from 'date-fns';
import {
  NgbDatepickerModule,
  NgbDate,
  NgbInputDatepicker,
  NgbDatepickerI18n,
} from '@ng-bootstrap/ng-bootstrap';
import { InputDateNgbDatepickerI18n } from './input-date-ngb-datepicker-i18n.service';
import { UiFormReactiveConfigService } from '../form-reactive-config.service';

let INPUT_UID = 0;

export type InputDateLabelSize = 'xs' | 'sm' | 'md';

export type InputDateValueType = Date | undefined | null;

@Component({
    selector: 'pxw-input-date',
    templateUrl: './input-date.component.html',
    styleUrl: './input-date.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: InputDateComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: InputDateComponent,
        },
        {
            provide: NgbDatepickerI18n,
            useClass: InputDateNgbDatepickerI18n,
        },
    ],
    imports: [
        NgClass,
        IconInfoComponent,
        IconCalendarComponent,
        InputMaskModule,
        NgbDatepickerModule,
    ]
})
export class InputDateComponent
  implements OnInit, OnChanges, ControlValueAccessor, Validator, WithDefaultValidatorProps
{
  @ViewChild('input') input: ElementRef<HTMLInputElement>;
  @ViewChild('inputDateNative') inputDateNative: ElementRef<HTMLInputElement>;
  @ViewChild(NgbInputDatepicker) inputDateNgb: NgbInputDatepicker;

  @Input({ required: true }) name: string;
  @Input() label: string;
  @Input() labelSize: InputDateLabelSize = 'sm';
  @Input() helper: string | null;
  @Input() placeholder = '';
  @Input() autofocus: boolean;
  @Input() messages: { [key: string]: string } = {};
  @Input() displayErrors = false;
  @Input() maskDateFormat = 'dd/MM/yyyy';
  @Input() inputDateFormat = 'yyyy-MM-dd';
  @Input() useNativePicker = false;

  @Input({ transform: booleanAttribute }) required?: boolean;
  @Input() minDate?: Date;
  @Input() maxDate?: Date;

  private injector = inject(Injector);

  protected value: InputDateValueType;
  protected inputId: string;
  protected isDisabled: boolean;
  protected dateMask: InputmaskOptions<Date>;

  private onChange: (value: InputDateValueType) => void;
  private onTouched: () => void;
  private onValidatorChange: () => void;
  private validatorFn: ValidatorFn | null;
  private formReactiveConfigService = inject(UiFormReactiveConfigService);

  @HostBinding('class') get classNames() {
    return clsx('ui-input', `ui-input-date`, {
      'ui-input--disabled': this.isDisabled,
      'ui-input--with-helper': this.helper,
      'ui-input--with-errors': !this.isValid,
    });
  }

  get formControl() {
    const ngControl = this.injector.get(NgControl, null);

    if (ngControl) {
      return ngControl.control as FormControl;
    }
    return null;
  }

  get isRequired() {
    return this.formControl?.hasValidator(Validators.required) || this.required === true;
  }

  get isValid() {
    return this.formControl?.valid;
  }

  get isTouched() {
    return this.formControl?.touched;
  }

  get errorEntries(): [string, any][] {
    return Object.entries(this.formControl?.errors || {});
  }

  get shouldDisplayErrors() {
    return (this.displayErrors || this.isTouched) && !this.isValid && !this.isDisabled;
  }

  get formattedDateValue() {
    return this.getFormattedDateValue(this.value, this.maskDateFormat);
  }

  get minInputDate(): string | null {
    if (this.minDate) {
      return format(this.minDate, this.inputDateFormat);
    }
    return null;
  }

  get maxInputDate(): string | null {
    if (this.maxDate) {
      return format(this.maxDate, this.inputDateFormat);
    }
    return null;
  }

  get minNgbDate(): NgbDate {
    return <NgbDate>this.dateToNgbDate(this.minDate);
  }

  get maxNgbDate(): NgbDate {
    return <NgbDate>this.dateToNgbDate(this.maxDate);
  }

  ngOnInit(): void {
    this.inputId = `input-date-${++INPUT_UID}`;

    this.dateMask = createMask<Date>({
      alias: 'datetime',
      inputFormat: 'dd/mm/yyyy',
      clearIncomplete: true,
      insertMode: false,
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      hasChangedAnyDefaultValidatorProps(changes) ||
      changes['minDate']?.currentValue !== changes['minDate']?.previousValue ||
      changes['maxDate']?.currentValue !== changes['maxDate']?.previousValue
    ) {
      this.validatorFn = composeValidatorFn(this);

      if (changes['minDate'] && changes['minDate'].currentValue) {
        this.validatorFn = Validators.compose([
          this.validatorFn,
          minDateValidator(changes['minDate'].currentValue),
        ]);
      }
      if (changes['maxDate'] && changes['maxDate'].currentValue) {
        this.validatorFn = Validators.compose([
          this.validatorFn,
          maxDateValidator(changes['maxDate'].currentValue),
        ]);
      }
    }
  }

  writeValue(value: InputDateValueType) {
    this.value = value;
  }
  registerOnChange(onChange: (value: InputDateValueType) => void) {
    this.onChange = onChange;
  }
  registerOnTouched(onTouched: () => void) {
    this.onTouched = onTouched;
  }
  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  validate(control: AbstractControl): ValidationErrors | null {
    return this.validatorFn?.(control) || null;
  }
  registerOnValidatorChange(onValidatorChange: () => void): void {
    this.onValidatorChange = onValidatorChange;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onInputDate($event: Event) {
    const value = (<HTMLInputElement>$event.target).value;
    const date = parse(value, this.maskDateFormat, new Date());
    const selectedDate = isValid(date) ? date : null;

    this.writeValue(selectedDate);
    this.onChange(selectedDate);
    this.onValidatorChange();
    this.onTouched();
  }

  onSelectedDateNativePicker($event: Event) {
    const value = (<HTMLInputElement>$event.target).value;
    const date = parse(value, this.inputDateFormat, new Date());
    const selectedDate = isValid(date) ? date : null;

    this.writeValue(selectedDate);
    this.writeValue(selectedDate);
    this.onChange(selectedDate);
    this.onValidatorChange();
    this.onTouched();
  }

  onSelectedDateNgbPicker(date: NgbDate | null) {
    const selectedDate = date
      ? parse(`${date.year}-${date.month}-${date.day}`, this.inputDateFormat, new Date())
      : null;

    this.writeValue(selectedDate);
    this.writeValue(selectedDate);
    this.onChange(selectedDate);
    this.onValidatorChange();
    this.onTouched();
  }

  openDatePicker() {
    if (this.isDisabled) {
      return;
    }
    if (this.useNativePicker) {
      this.inputDateNative.nativeElement.showPicker?.();
    } else {
      if (this.value) {
        this.inputDateNgb.manualDateChange(format(this.value, this.inputDateFormat));
      }
      this.inputDateNgb.open();
    }
  }

  focus() {
    this.input.nativeElement.focus();
  }

  protected getErrorMessage([key, data]: [string, any]) {
    return this.messages?.[key] || this.formReactiveConfigService.getErrorMessage(key, data);
  }

  protected dateToNgbDate(value?: Date | null): NgbDate | null {
    if (!value) {
      return null;
    }
    const day = parseInt(format(value, 'dd'), 10);
    const month = parseInt(format(value, 'MM'), 10);
    const year = parseInt(format(value, 'yyyy'), 10);

    return new NgbDate(year, month, day);
  }

  private getFormattedDateValue(value: Date | null | undefined, dateFormat: string): string | null {
    if (value instanceof Date) {
      return format(value, dateFormat);
    }
    return null;
  }
}

export function minDateValidator(minDate: Date): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value || isBefore(minDate, control.value) || isEqual(minDate, control.value)) {
      return null;
    }
    return { minDate: true };
  };
}

export function maxDateValidator(maxDate: Date): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value || isAfter(maxDate, control.value) || isEqual(maxDate, control.value)) {
      return null;
    }
    return { maxDate: true };
  };
}
